import React, { useEffect, useState, useCallback } from "react";
import client, { apiErr } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useNavigate } from "react-router-dom";
import { LANGS, TL } from "../i18n";
import { Card, Button, Input, Select, Badge } from "../components/ui";
import { Headphones, Phone, Mail, Users, Building2, Globe, Radio, Plus, Trash2, CheckCircle2, UserPlus, Inbox, ShieldCheck, Zap } from "lucide-react";

const langLabel = (code) => (LANGS.find((l) => l.code === code)?.label || code);

export default function WorkRouting() {
  const { user, refreshUser, isSenior } = useApp();
  const navigate = useNavigate();
  const [pres, setPres] = useState(null);
  const [onShift, setOnShift] = useState(!!user?.on_shift);
  const [tab, setTab] = useState("queue");
  const [work, setWork] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [agentSort, setAgentSort] = useState("language");
  const [langFilter, setLangFilter] = useState("");
  const [inboxLang, setInboxLang] = useState("");
  const [call, setCall] = useState({ title: "", detail: "", language: "", agent_id: "" });
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const loadPresence = useCallback(() => {
    client.get("/manager/presence").then((r) => setPres(r.data)).catch((e) => {
      if (e?.response?.status === 403) { navigate("/app", { replace: true }); return; }
      setErr(apiErr(e));
    });
  }, [navigate]);
  const loadWork = useCallback(() => {
    client.get("/manager/work").then((r) => setWork(r.data.items || [])).catch(() => {});
  }, []);
  const loadInbox = useCallback(() => {
    client.get(`/manager/inbox?language=${inboxLang}`).then((r) => setInbox(r.data.items || [])).catch(() => {});
  }, [inboxLang]);

  useEffect(() => {
    if (user && !isSenior) navigate("/app", { replace: true });
  }, [user, isSenior, navigate]);

  useEffect(() => { loadPresence(); loadWork(); }, [loadPresence, loadWork]);
  useEffect(() => { if (tab === "inbox") loadInbox(); }, [tab, loadInbox]);

  const toggleShift = async () => {
    const next = !onShift; setOnShift(next);
    try { await client.post("/presence/ping", { on_shift: next }); refreshUser && refreshUser(); loadPresence(); }
    catch (e) { setErr(apiErr(e)); }
  };

  const staff = pres?.staff || [];
  const agents = staff
    .filter((s) => !langFilter || (s.languages || []).includes(langFilter))
    .sort((a, b) => {
      if (agentSort === "language") return ((a.languages || [])[0] || "~").localeCompare((b.languages || [])[0] || "~");
      if (agentSort === "online") return (b.online - a.online) || (a.name || "").localeCompare(b.name || "");
      return (a.name || "").localeCompare(b.name || "");
    });

  const createCall = async (smart = false) => {
    setErr(""); setMsg("");
    if (!call.title.trim()) { setErr("Give the call a short title."); return; }
    try {
      const { data } = await client.post("/manager/work", { kind: "call", title: call.title, detail: call.detail, language: call.language, agent_id: call.agent_id || null });
      const created = data.item;
      if (smart && created && !created.assigned_to) {
        const a = bestAgentFor(created.language);
        if (a) { await client.post(`/manager/work/${created.id}/assign`, { agent_id: a.id }); setMsg(`Call added and smart-assigned → ${a.name}.`); }
        else { setMsg("Call added. No online agent to smart-assign right now."); }
      } else {
        setMsg("Call added to the queue.");
      }
      setCall({ title: "", detail: "", language: "", agent_id: "" }); loadWork();
    } catch (e) { setErr(apiErr(e)); }
  };
  const assignWork = async (id, agent_id) => {
    if (!agent_id) return;
    try { await client.post(`/manager/work/${id}/assign`, { agent_id }); loadWork(); } catch (e) { setErr(apiErr(e)); }
  };
  // Smart assign: first ON-SHIFT+online agent whose languages include the item's language,
  // then any online agent, else null. Language-aware routing for the future phone/email system.
  const bestAgentFor = (language) => {
    const pool = agents;
    if (language) {
      // Language-aware routing: never misroute a caller to an agent who doesn't speak their
      // language. If none is available, return null so smartAssign surfaces a clear error and
      // the senior can assign manually.
      return pool.find((a) => a.on_shift && a.online && (a.languages || []).includes(language))
        || pool.find((a) => a.online && (a.languages || []).includes(language))
        || null;
    }
    return pool.find((a) => a.on_shift && a.online) || pool.find((a) => a.online) || null;
  };
  const smartAssign = async (w) => {
    setErr(""); setMsg("");
    const a = bestAgentFor(w.language);
    if (!a) { setErr(w.language ? `No online agent available for ${langLabel(w.language)}.` : "No online agent available right now."); return; }
    await assignWork(w.id, a.id);
    setMsg(`Smart-assigned "${w.title}" → ${a.name}.`);
  };
  const setStatus = async (id, status) => {
    try { await client.post(`/manager/work/${id}/status`, { status }); loadWork(); } catch (e) { setErr(apiErr(e)); }
  };
  const delWork = async (id) => {
    try { await client.delete(`/manager/work/${id}`); loadWork(); } catch { /* noop */ }
  };
  const assignEmail = async (m, agent_id) => {
    if (!agent_id) return;
    setErr(""); setMsg("");
    try {
      await client.post("/manager/work", { kind: "email", title: `Reply: ${m.subject || "message"}`, detail: m.message, language: m.lang, message_id: m.id, agent_id });
      setMsg(`Email assigned for review.`); loadInbox(); loadWork();
    } catch (e) { setErr(apiErr(e)); }
  };

  const AgentPicker = ({ value, onChange, testid }) => (
    <Select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      <option value="">{TL("— assign to agent —")}</option>
      {agents.map((a) => <option key={a.id} value={a.id}>{a.name}{a.languages?.length ? ` (${a.languages.map(langLabel).join("/")})` : ""}{a.online ? " · online" : ""}</option>)}
    </Select>
  );

  return (
    <div className="space-y-6 fadeup" data-testid="work-routing-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-head text-2xl sm:text-3xl flex items-center gap-2"><Headphones size={24} /> {TL("Work Routing")}</h1>
        <span className="opacity-60 text-sm">{TL("Route calls and emails to agents in your office(s). Telephony connects to your phone system later — for now this is the shared work queue.")}</span>
        <button onClick={toggleShift} data-testid="shift-toggle"
          className={`ms-auto flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm lift ${onShift ? "border-[#24A148] bg-[#24A148]/10 text-[#24A148]" : "border-black/15 dark:border-white/15 opacity-70"}`}>
          <Radio size={15} className={onShift ? "animate-pulse" : ""} /> {onShift ? "On shift" : "Off shift"}
        </button>
      </div>

      {err && <Card className="border-[#DA1E28]/40 text-sm text-[#DA1E28]" data-testid="wr-err">{err}</Card>}
      {msg && <Card className="border-[var(--primary)]/40 text-sm" data-testid="wr-msg">{msg}</Card>}

      {/* Presence board */}
      <Card className="space-y-3" data-testid="presence-board">
        <div className="flex items-center gap-2 font-head"><Users size={18} className="text-[var(--primary)]" /> On shift now
          <Badge color="success" data-testid="presence-online">{pres?.totals?.on_shift || 0} on shift · {pres?.totals?.online || 0} online</Badge>
        </div>
        {(pres?.by_office || []).length === 0 && <p className="text-sm opacity-60">{TL("No offices assigned to you yet, or no staff in your office(s).")}</p>}
        <div className="grid gap-3 md:grid-cols-2">
          {(pres?.by_office || []).map((o) => (
            <div key={o.office_id} className="rounded-md border border-black/10 p-3 dark:border-white/10" data-testid={`presence-office-${o.office_id}`}>
              <div className="mb-1 flex items-center gap-2 text-sm font-medium"><Building2 size={14} /> {o.office}
                <span className="ms-auto text-xs opacity-60">{o.on_shift}/{o.total} on shift</span>
              </div>
              <div className="flex flex-wrap gap-1.5 text-xs">
                {Object.entries(o.by_language).map(([l, s]) => (
                  <span key={l} className="flex items-center gap-1 rounded-full bg-[var(--primary)]/10 px-2 py-0.5 text-[var(--primary)]">
                    <Globe size={10} />{langLabel(l)} · {s.online}/{s.total}
                  </span>
                ))}
                {Object.keys(o.by_language).length === 0 && <span className="opacity-40">{TL("no staff")}</span>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2" data-testid="wr-tabs">
        {[["queue", "Work queue", Phone], ["inbox", "Email inbox", Inbox]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`wr-tab-${k}`}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm lift ${tab === k ? "border-[var(--primary)] bg-[var(--primary)] text-white" : "border-black/15 dark:border-white/15"}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "queue" && (
        <>
          <Card className="space-y-3" data-testid="new-call">
            <div className="flex items-center gap-2 font-head"><Phone size={18} className="text-[var(--primary)]" /> {TL("Log / route a call")}</div>
            <div className="grid gap-2 md:grid-cols-2">
              <Input label={TL("Title")} placeholder={TL("e.g. Callback: billing question")} value={call.title} onChange={(e) => setCall({ ...call, title: e.target.value })} data-testid="call-title" />
              <Select label={TL("Language")} value={call.language} onChange={(e) => setCall({ ...call, language: e.target.value })} data-testid="call-language">
                <option value="">{TL("— any —")}</option>
                {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </Select>
            </div>
            <Input label={TL("Notes (optional)")} value={call.detail} onChange={(e) => setCall({ ...call, detail: e.target.value })} data-testid="call-detail" />
            <div className="grid gap-2 md:grid-cols-2">
              <AgentPicker value={call.agent_id} onChange={(v) => setCall({ ...call, agent_id: v })} testid="call-agent" />
              <div className="flex justify-end gap-2">
                <Button variant="subtle" onClick={() => createCall(true)} data-testid="call-smart-submit"><Zap size={15} /> {TL("Add & smart-assign")}</Button>
                <Button onClick={() => createCall(false)} data-testid="call-submit"><Plus size={15} /> {TL("Add to queue")}</Button>
              </div>
            </div>
          </Card>

          <Card className="space-y-2" data-testid="work-list">
            <div className="font-head">Queue ({work.length})</div>
            {work.length === 0 ? <p className="text-sm opacity-60">{TL("Nothing in the queue.")}</p> : (
              <ul className="divide-y divide-black/5 dark:divide-white/5">
                {work.map((w) => (
                  <li key={w.id} className="flex flex-wrap items-center gap-2 py-2 text-sm" data-testid={`work-${w.id}`}>
                    {w.kind === "email" ? <Mail size={14} className="text-[var(--primary)]" /> : <Phone size={14} className="text-[var(--primary)]" />}
                    <span className="font-medium">{w.title}</span>
                    {w.language && <span className="flex items-center gap-1 rounded-full bg-[var(--primary)]/10 px-2 py-0.5 text-xs text-[var(--primary)]"><Globe size={10} />{langLabel(w.language)}</span>}
                    <Badge color={w.status === "done" ? "success" : w.status === "assigned" ? "info" : "neutral"}>{w.status}</Badge>
                    {w.assigned_to_name && <span className="text-xs opacity-70">→ {w.assigned_to_name}</span>}
                    <div className="ms-auto flex items-center gap-1.5">
                      {w.status === "open" && <button onClick={() => smartAssign(w)} data-testid={`work-smart-${w.id}`} className="flex items-center gap-1 rounded-full border border-[var(--primary)]/40 px-2 py-0.5 text-xs text-[var(--primary)] lift" title={TL("Smart assign by language")}><Zap size={12} /> {TL("Smart")}</button>}
                      <select onChange={(e) => { assignWork(w.id, e.target.value); e.target.value = ""; }} defaultValue="" data-testid={`work-assign-${w.id}`}
                        className="rounded-md border border-black/15 bg-transparent px-1.5 py-1 text-xs dark:border-white/15">
                        <option value="">{TL("assign…")}</option>
                        {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                      {w.status !== "done" && <button onClick={() => setStatus(w.id, "done")} data-testid={`work-done-${w.id}`} className="text-[#24A148] hover:opacity-70" title={TL("Mark done")}><CheckCircle2 size={16} /></button>}
                      <button onClick={() => delWork(w.id)} data-testid={`work-del-${w.id}`} className="text-[#DA1E28] hover:opacity-70"><Trash2 size={15} /></button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Agents (language sort) */}
          <Card className="space-y-2" data-testid="agents-panel">
            <div className="flex flex-wrap items-center gap-2 font-head"><UserPlus size={18} className="text-[var(--primary)]" /> My office agents ({agents.length})
              <div className="ms-auto flex items-center gap-2">
                <Select value={langFilter} onChange={(e) => setLangFilter(e.target.value)} data-testid="agent-lang-filter">
                  <option value="">{TL("All languages")}</option>
                  {(pres?.staff ? [...new Set(staff.flatMap((s) => s.languages || []))] : []).map((l) => <option key={l} value={l}>{langLabel(l)}</option>)}
                </Select>
                <Select value={agentSort} onChange={(e) => setAgentSort(e.target.value)} data-testid="agent-sort">
                  <option value="language">{TL("Sort: Language")}</option>
                  <option value="online">{TL("Sort: Online first")}</option>
                  <option value="name">{TL("Sort: Name")}</option>
                </Select>
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {agents.length === 0 && <p className="text-sm opacity-60">{TL("No agents in your office(s) yet.")}</p>}
              {agents.map((a) => (
                <div key={a.id} className="flex items-center gap-2 rounded-md border border-black/10 px-3 py-2 text-sm dark:border-white/10" data-testid={`agent-${a.id}`}>
                  <span className={`h-2 w-2 rounded-full ${a.online ? "bg-[#24A148]" : "bg-black/20 dark:bg-white/20"}`} title={a.online ? "online" : "offline"} />
                  <span className="font-medium">{a.name}</span>
                  {a.role_name && <Badge color="neutral">{a.role_name}</Badge>}
                  {a.on_shift && <ShieldCheck size={13} className="text-[#24A148]" title={TL("on shift")} />}
                  <span className="ms-auto flex flex-wrap gap-1">
                    {(a.languages || []).map((l) => <span key={l} className="rounded-full bg-[var(--primary)]/10 px-1.5 py-0.5 text-[10px] text-[var(--primary)]">{langLabel(l)}</span>)}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {tab === "inbox" && (
        <Card className="space-y-3" data-testid="inbox-panel">
          <div className="flex flex-wrap items-center gap-2 font-head"><Inbox size={18} className="text-[var(--primary)]" /> Contact emails
            <Select className="ms-auto" value={inboxLang} onChange={(e) => setInboxLang(e.target.value)} data-testid="inbox-lang">
              <option value="">{TL("All languages")}</option>
              {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </Select>
          </div>
          {inbox.length === 0 ? <p className="text-sm opacity-60">{TL("No messages in the arrivals inbox.")}</p> : (
            <ul className="divide-y divide-black/5 dark:divide-white/5">
              {inbox.map((m) => (
                <li key={m.id} className="space-y-1 py-2 text-sm" data-testid={`inbox-${m.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Mail size={14} className="text-[var(--primary)]" />
                    <span className="font-medium">{m.subject || "(no subject)"}</span>
                    <span className="text-xs opacity-60">{m.name} · {m.email}</span>
                    {m.lang && <span className="rounded-full bg-[var(--primary)]/10 px-1.5 py-0.5 text-[10px] text-[var(--primary)]">{langLabel(m.lang)}</span>}
                    {m.work_assigned_to
                      ? <Badge color="info" data-testid={`inbox-assigned-${m.id}`}>→ {m.work_assigned_to}</Badge>
                      : <select onChange={(e) => { assignEmail(m, e.target.value); e.target.value = ""; }} defaultValue="" data-testid={`inbox-assign-${m.id}`}
                          className="ms-auto rounded-md border border-black/15 bg-transparent px-1.5 py-1 text-xs dark:border-white/15">
                          <option value="">{TL("assign for reply…")}</option>
                          {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                        </select>}
                  </div>
                  <p className="line-clamp-2 text-xs opacity-70">{m.message}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}
